import QuestionValue from "../QuestionValue";
import QuestionValueFraction from "../QuestionValueFraction";
import QuestionValuePercentage from "../QuestionValuePercentage";
import QuestionModule from "./QuestionModule";
import QuestionModel from "../QuestionModel";

export default class QuestionModuleSorting extends QuestionModule {
    constructor() {
        super();
    }

    create(data, ascending) {
        const question = super.create(data);
        question.ascending = typeof ascending === "undefined" ? P3.math.randomBool() : ascending;

        switch (data.type) {
            default : {
                console.warn("Question model type incompatible with module:", data.type);
                return question;
            }
            case QuestionModel.TYPE_SORT_INTEGER: {
                return this.createWholeNumber(question, data);
            }
            case QuestionModel.TYPE_SORT_FRACTION: {
                return this.createFraction(question, data);
            }
            case QuestionModel.TYPE_SORT_DECIMAL: {
                return this.createDecimal(question, data);
            }
            case QuestionModel.TYPE_MIXED: {
                return this.createMixed(question, data);
            }
        }
    }

    createWholeNumber(question, data) {
        let k = 1000;
        const rounding = data.rounding ? data.rounding : 10;
        const pivotValue = Math.floor(P3.math.randomInt(data.range.min, data.range.max) / rounding) * rounding;
        while (question.values.length < data.values && k-- > 0) {
            const n = pivotValue + P3.math.randomInt(0, data.rounding - 1);
            if (!question.values.length || question.values.every(x => x.value !== n)) {
                question.values.push(new QuestionValue(n));
            }
        }
        question.values.sort((a, b) => {
            return question.ascending ? a.value - b.value : b.value - a.value;
        });
        return question;
    }

    createFraction(question, data) {

        //ensure either the same denominator, or that the numerator is 1 (bbc feedback)
        if (data.difficulty < 2) {
            const numeratorIsOne = Math.random() < 0.5;
            let randomDenominator = P3.math.randomInt(data.range_denominator.min, data.range_denominator.max);
            if (randomDenominator === 1) randomDenominator++;

            let k = 1000;
            while (question.values.length < data.values && k-- > 0) {
                const numerator = numeratorIsOne ? 1 : P3.math.randomInt(data.range.min, data.range.max);
                let denominator = numeratorIsOne ? P3.math.randomInt(data.range_denominator.min, data.range_denominator.max) : randomDenominator;

                if (denominator === 1) denominator++;

                if (denominator !== numerator) {
                    if (numerator < denominator) {
                        if (question.values.every(x => parseFloat(x.value) !== numerator / denominator))
                            question.values.push(new QuestionValueFraction(numerator, denominator));
                    }
                }
            }

            question.values.sort((a, b) => {
                return question.ascending ? a.value - b.value : b.value - a.value;
            });

        } else if (data.difficulty === 2) {
            const numeratorIsOne = Math.random() < 0.5;
            let randomDenominator = P3.math.randomInt(data.range_denominator.min, data.range_denominator.max);
            if (randomDenominator === 1) randomDenominator++;

            let k = 1000;
            while (question.values.length < data.values && k-- > 0) {
                const numerator = numeratorIsOne ? 1 : P3.math.randomInt(data.range.min, data.range.max);
                let denominator = numeratorIsOne ? P3.math.randomInt(data.range_denominator.min, data.range_denominator.max) : randomDenominator;

                if (denominator === 1) denominator++;

                if (Math.random() < 0.35) denominator *= P3.math.randomInt(2, 3);

                if (denominator !== numerator) {
                    if (numerator < denominator) {
                        if (question.values.every(x => parseFloat(x.value) !== numerator / denominator))
                            question.values.push(new QuestionValueFraction(numerator, denominator));
                    }
                }
            }

            question.values.sort((a, b) => {
                return question.ascending ? a.value - b.value : b.value - a.value;
            });

        } else {
            let k = 1000;
            while (question.values.length < data.values && k-- > 0) {
                const numerator = P3.math.randomInt(data.range.min, data.range.max);
                let denominator = P3.math.randomInt(data.range_denominator.min, data.range_denominator.max);

                if (denominator === 1) denominator++;

                if (denominator !== numerator) {
                    if (numerator < denominator) {
                        if (question.values.every(x => parseFloat(x.value) !== numerator / denominator))
                            question.values.push(new QuestionValueFraction(numerator, denominator));
                    }
                }
            }

            question.values.sort((a, b) => {
                return question.ascending ? a.value - b.value : b.value - a.value;
            });
        }

        return question;
    }

    createDecimal(question, data) {
        //generate random decimals in range, ensure is to one dp and isn't a whole number
        const dp = data.dp ? data.dp : 1;

        let k = 1000;
        while (question.values.length < data.values && k-- > 0) {
            const decimal = P3.math.random(data.range.min, data.range.max);
            if (decimal % 1 !== 0) {
                //ensure is correct dp
                const decimalToDp = parseFloat(decimal.toFixed(dp));
                if (decimalToDp % 1 !== 0) {
                    if (question.values.every(x => x.value !== decimalToDp))
                        question.values.push(new QuestionValue(decimalToDp));
                }
            }
        }

        question.values.sort((a, b) => {
            return question.ascending ? a.value - b.value : b.value - a.value;
        });
        return question;
    }

    createMixed(question, data) {

        const denominator = P3.utils.selectRandom([2, 4, 10]);

        let k = 100;
        const arr = [];
        while (k-- > 0 && question.values.length < data.values) {
            //make random number that is multiple of ten less than 101
            const value = P3.math.randomInt(1, 9);

            const r = Math.random();
            if (r < 0.33) {
                //its a percent
                if (arr.indexOf(value / 10) === -1) {
                    question.values.push(new QuestionValuePercentage(value, 10));
                    arr.push(value / 10);
                }
            }
            else if (r < 0.66) {
                //its a decimal
                if (arr.indexOf(value / 10) === -1) {
                    question.values.push(new QuestionValue(value / 10));
                    arr.push(value / 10);
                }
            }
            else {
                //its a fraction
                if (value < denominator && arr.indexOf(value / denominator) === -1) {
                    question.values.push(new QuestionValueFraction(value, denominator));
                    arr.push(value / denominator);
                }
            }
        }

        question.values.sort((a, b) => {
            return question.ascending ? a.value - b.value : b.value - a.value;
        });

        return question;
    }
}